/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import { Form, Formik } from 'formik';

import { useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import styles from './contract-line-customer-header.module.scss';
import { validationSchema } from './validationSchema';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';

import {
  clearLinesSelection,
  triggerLineRefresh,
  useSelectedLines,
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import {
  clearVariableLinesSelection,
  triggerVariableRefresh,
  useSelectedVariableLines,
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import PartyColumn from '@/pages/ContractDetails/ContractParties/components/ParentHeaders/PartyColumn';
import { rightsidePanel_isOpen } from '@/pages/ContractDetails/RightPanel/RightPanel.state';
import { useBulkUpdateContractLineShipToInvoiceTo } from '@/pages/ContractDetails/ContractLines/hooks/useBulkUpdateContractLineShipToInvoiceTo';
import { useLoadingAnimation } from '@/components/LoadingAnimation/useLoadingAnimation.hook';
import useGetPurchaseOrders from '@/pages/ContractDetails/ContractLines/hooks/useGetPurchaseOrders';
import PurchaseOrderColumn from './PurchaseOrderColumn';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';

interface IHeadersProps {
  contractId: string;
  contract: ContractCommonData | undefined;
  onClose: () => void;
}

interface IFormValues {
  shipToCustomer: ContractCommonData['customer'] | undefined | null;
  invoiceToCustomer: ContractCommonData['customer'] | undefined | null;
  purchaseOrderId: UUID | undefined;
}

export default function ContractLineCustomerHeader({
  contractId,
  contract,
  onClose,
}: IHeadersProps) {
  const searchShipTo = useSearchCustomers();
  const searchInvoiceTo = useSearchCustomers();

  const { getPurchaseOrdersItems, getPurchaseOrdersLoading } =
    useGetPurchaseOrders();

  const shipToRef = useRef<any>(null);
  const invoiceToRef = useRef<any>(null);

  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();

  const bulkUpdateClShipToInvoiceTo =
    useBulkUpdateContractLineShipToInvoiceTo(contractId);
  const { toggleLoadingAnimation } = useLoadingAnimation();

  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const normalizeId = (id: string | undefined | null): string | undefined => {
    if (!id || id === '') {
      return undefined;
    }
    return id as string;
  };

  const handleSuccess = () => {
    clearLinesSelection();
    clearVariableLinesSelection();
    setIsOpenRightPanel(false);
  };

  const onSubmit = async (values: IFormValues) => {
    const newShipId = normalizeId(values.shipToCustomer?.customerID);
    const newInvoiceId = normalizeId(values.invoiceToCustomer?.customerID);
    const newPurchaseOrderId = normalizeId(values.purchaseOrderId);

    const toUpdateVariableLines = selectedVariableLineRows.length > 0;

    toggleLoadingAnimation(true);

    await bulkUpdateClShipToInvoiceTo(
      [
        ...selectedLineRows.map((cl) => cl.id),
        ...selectedVariableLineRows.map((cl) => cl.id),
      ],
      newShipId,
      newInvoiceId,
      newPurchaseOrderId,
      toUpdateVariableLines
    );

    setShowConfirmationModal(false);
    onClose();
    handleSuccess();
    triggerLineRefresh();
    triggerVariableRefresh();

    toggleLoadingAnimation(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          shipToCustomer: undefined as
            | ContractCommonData['customer']
            | undefined
            | null,
          invoiceToCustomer: undefined as
            | ContractCommonData['customer']
            | undefined
            | null,
          purchaseOrderId: undefined as UUID | undefined,
        }}
        enableReinitialize={true}
        onSubmit={async (values) => {

          if (!values.purchaseOrderId) {
            setShowConfirmationModal(true);
          }
          else {
            await onSubmit(values);
          }
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
          <Form>
            <AgConfirmationModal
              isOpen={showConfirmationModal}
              title={'Confirm ship to / invoice to account update'}
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={async () => {
                await onSubmit(values);
              }}
              contentStyle={{ width: 600 }}
            >
              You are updating a contract line that requires a Purchase Order, please assign one, otherwise this will reset to null.<br />
              Are you sure you want to continue?
            </AgConfirmationModal>

            <div>
              <div className={styles['header-wrapper']}>
                <PartyColumn
                  ref={shipToRef}
                  fieldName="Ship To"
                  fieldKey="shipToCustomer"
                  customerParty={undefined}
                  isEditable={true}
                  isRequired={false}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  searchCus={searchShipTo}
                />

                <PartyColumn
                  ref={invoiceToRef}
                  fieldName="Invoice To"
                  fieldKey="invoiceToCustomer"
                  customerParty={undefined}
                  isEditable={true}
                  isRequired={false}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  searchCus={searchInvoiceTo}
                />

                <PurchaseOrderColumn
                  ref={invoiceToRef}
                  fieldName="Purchase Order"
                  fieldKey="purchaseOrderId"
                  isEditable={!!values.invoiceToCustomer?.customerAccountNumber}
                  isRequired={false}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  searchPos={{
                    getPurchaseOrdersItems,
                    getPurchaseOrdersLoading,
                  }}
                  customerCode={values.invoiceToCustomer?.customerAccountNumber}
                  currency={contract?.currency?.currency}
                  customerName={values.invoiceToCustomer?.customerName}
                  customerId={values.invoiceToCustomer?.customerID}
                />
              </div>
              <div className={styles['party-header-save-btn']}>
                <Button
                  type="submit"
                  buttonStyleType={ButtonStyleType.PRIMARY}
                  disabled={
                    !(
                      normalizeId(values.shipToCustomer?.customerID) ||
                      normalizeId(values.invoiceToCustomer?.customerID)
                    )
                  }
                >
                  Save
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
